import { useEffect, useMemo, useState } from 'react';

import type { DashboardDefensorResumoDto } from '@/api.types';
import { BuscarResumoHomeDefensor } from '@/modules/home/application/defensor-home.service';
import DefensorHomeResumo, { DefensorHomeSaudacao } from '@/modules/home/components/DefensorHomeResumo';
import ProfileHomeCard from '@/modules/home/components/ProfileHomeCard';
import { getProfileHomeItems } from '@/modules/home/profile-home.utils';
import { useMeusPaineisGerenciais } from '@/modules/system/painel-gerencial/use-meus-paineis-gerenciais';
import Loading from '@/shared/components/Carregando';
import { NavigationView } from '@/shared/constants/navigation-view.enum';
import { useNavigationView } from '@/shared/hooks/useNavigationView';

const VISOES_HOME_DEFENSOR = new Set([NavigationView.Defensor, NavigationView.DefensorDiretor]);

export default function ProfileHomePage() {
  const { effectiveView, viewLabel, viewReady, permissions } = useNavigationView();
  const paineisGerenciais = useMeusPaineisGerenciais();
  const [resumo, setResumo] = useState<DashboardDefensorResumoDto | null>(null);
  const [resumoLoading, setResumoLoading] = useState(false);
  const [resumoErro, setResumoErro] = useState(false);

  const ehHomeDefensor = effectiveView != null && VISOES_HOME_DEFENSOR.has(effectiveView);

  const items = useMemo(() => {
    if (effectiveView == null) return [];
    return getProfileHomeItems(effectiveView, permissions, paineisGerenciais);
  }, [effectiveView, permissions, paineisGerenciais]);

  useEffect(() => {
    if (!viewReady || !ehHomeDefensor) {
      setResumo(null);
      setResumoErro(false);
      setResumoLoading(false);
      return;
    }

    let cancelled = false;
    setResumoLoading(true);
    setResumoErro(false);
    BuscarResumoHomeDefensor()
      .then((data) => {
        if (cancelled) return;
        setResumo(data);
        setResumoErro(data == null);
      })
      .catch(() => {
        if (!cancelled) {
          setResumo(null);
          setResumoErro(true);
        }
      })
      .finally(() => {
        if (!cancelled) setResumoLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [viewReady, ehHomeDefensor]);

  if (!viewReady || effectiveView == null) {
    return <Loading />;
  }

  return (
    <div className="container py-4">
      {ehHomeDefensor ? (
        <section className="defensor-home-cabecalho mb-3">
          <DefensorHomeSaudacao resumo={resumo} loading={resumoLoading} />
          <p className="text-muted mb-0">
            Selecione abaixo a área do sistema que deseja acessar nesta sessão.
          </p>
        </section>
      ) : (
        <>
          <section className="text-center mb-4">
            <h2 className="mt-1 mb-1" style={{ color: '#1f2d24', fontWeight: 700 }}>
              {viewLabel}
            </h2>
          </section>
          <section className="text-center mb-4">
            <div className="text-muted" style={{ maxWidth: 860, margin: '0 auto' }}>
              Selecione abaixo a área do sistema que deseja acessar nesta sessão.
            </div>
          </section>
        </>
      )}

      <div className="escolha-modulo-wrap escolha-modulo-wrap--profile-home">
        <ul className="escolha-modulo-grid escolha-modulo-grid--3cols">
          {items.map((item) => (
            <li key={`${item.label}-${item.to}`}>
              <ProfileHomeCard
                label={item.label}
                description={item.description}
                to={item.to}
              />
            </li>
          ))}
        </ul>
      </div>

      {ehHomeDefensor && (
        <DefensorHomeResumo resumo={resumo} loading={resumoLoading} erro={resumoErro} />
      )}
    </div>
  );
}
